import React from 'react';
import { Box } from '@mui/material';

import { color, motion } from '../../../theme/tokens';
import { GENERATING } from '../copy';

/**
 * The synthesis-node mark, animated, plus "Generating your stack…" — or, with
 * `label={null}`, the mark alone, sized and coloured by its host.
 *
 * Geometry and choreography are transcribed from the design system brief's own
 * mark (`Brand_Design/mystack-design-system-brief-Aug.html`, §Mark animation):
 * three nodes pulsing on a staggered loop at 0s / 0.22s / 0.44s, `pulse`
 * scaling .76 → 1.18 and fading .36 → 1 over `--t-loop` on `--ease`, infinite;
 * the link lines drawing on `drawln`, alternating.
 *
 * `--t-loop`'s comment in `tokens.js` reserves it for the synthesis-node loop
 * during stack generation and the interaction checker's check ONLY. Those are
 * its two uses: this card's CTA, and the checker page's checking screen
 * (v5.1, STACK-587), which passes a larger `size`, the brief's two-tone
 * colours (`markColor`, `synthesisColor`) and no label, and says what is
 * happening in its own copy.
 *
 * By default it sits inside the accent CTA, so every stroke and fill is `--accent-ink`
 * rather than the brief's on-dark soft accent — white and soft mint both fail
 * AA on `--accent` at this size, and the brief's own button rule pairs that
 * green with the accent-ink token.
 *
 * Under `prefers-reduced-motion: reduce` every animation is switched off and
 * the mark renders at rest — the nodes still read as a mark, the copy still
 * says what is happening, nothing moves. `animation: none` is applied through
 * the media query rather than by not emitting the keyframes, so the resting
 * geometry is identical in both cases.
 */

const NODES = [
  { cx: 16, cy: 20, r: 6, delay: '0s' },
  { cx: 50, cy: 14, r: 6, delay: '0.22s' },
  { cx: 40, cy: 46, r: 9, delay: '0.44s' },
];

const LINKS = [
  { x1: 16, y1: 20, x2: 50, y2: 14 },
  { x1: 16, y1: 20, x2: 40, y2: 46 },
  { x1: 50, y1: 14, x2: 40, y2: 46 },
];

const REDUCED = '@media (prefers-reduced-motion: reduce)';

// The large node, where the links meet.
const SYNTHESIS_NODE = 2;

export default function GeneratingMark({
  label = GENERATING,
  size = '1rem',
  markColor = color.accentInk,
  // The synthesis node (the large one). One colour in the CTA; the checker
  // page draws the brief's two-tone mark: evergreen links and outer nodes,
  // the synthesis node accent.
  synthesisColor = markColor,
}) {
  // Without a label the host announces the state itself.
  const announces = label !== null;
  return (
    <Box
      // The CTA slot is a live region for the whole card: the state it reports
      // arrives without the user acting, so it has to announce itself.
      role={announces ? 'status' : undefined}
      aria-live={announces ? 'polite' : undefined}
      data-testid="generating-state"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        color: color.accentInk,
        fontWeight: 700,
        fontSize: '0.875rem',
        lineHeight: 1.2,
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 64 64"
        aria-hidden="true"
        focusable="false"
        data-testid="synthesis-mark"
        sx={{
          width: size, // 16px in the CTA, per the ruling
          height: size,
          flexShrink: 0,
          overflow: 'visible',
          '@keyframes synthesis-pulse': {
            '0%, 100%': { transform: 'scale(.76)', opacity: 0.36 },
            '50%': { transform: 'scale(1.18)', opacity: 1 },
          },
          '@keyframes synthesis-drawln': {
            from: { strokeDashoffset: 48 },
            to: { strokeDashoffset: 0 },
          },
          '& .synthesis-node': {
            // Scale about the node's own centre rather than the SVG origin,
            // which is what makes three pulses read as three nodes breathing
            // instead of the whole mark swelling.
            transformBox: 'fill-box',
            transformOrigin: 'center',
            animationName: 'synthesis-pulse',
            animationDuration: motion.loop,
            animationTimingFunction: motion.ease,
            animationIterationCount: 'infinite',
          },
          '& .synthesis-link': {
            strokeDasharray: 48,
            animationName: 'synthesis-drawln',
            animationDuration: motion.loop,
            animationTimingFunction: motion.ease,
            animationIterationCount: 'infinite',
            animationDirection: 'alternate',
          },
          [REDUCED]: {
            '& .synthesis-node, & .synthesis-link': {
              animation: 'none',
              // Without this the nodes would rest at the keyframe-free default
              // rather than at full opacity, and the mark would read as dimmed
              // for exactly the users who cannot see it brighten.
              opacity: 1,
              strokeDashoffset: 0,
            },
          },
        }}
      >
        {LINKS.map((link, index) => (
          <line
            key={`link-${index}`}
            className="synthesis-link"
            {...link}
            stroke={markColor}
            strokeWidth="2.2"
          />
        ))}
        {NODES.map((node, index) => (
          <circle
            key={`node-${index}`}
            className="synthesis-node"
            cx={node.cx}
            cy={node.cy}
            r={node.r}
            fill={index === SYNTHESIS_NODE ? synthesisColor : markColor}
            style={{ animationDelay: node.delay }}
          />
        ))}
      </Box>
      {announces && label}
    </Box>
  );
}
